import * as React from "react";
import { AtSign, ChevronDown } from "lucide-react";

import { MailboxList } from "@/components/containers/MailboxList";
import type { ContainerSummaryDto, EnrollmentDto } from "@/types/api";

/**
 * Plan-level email management, embedded in the Overview plan accordion.
 * Each domain in the plan gets an expandable row hosting the full mailbox
 * manager (live list from 20i, create, delete, connection details) — so the
 * client sees and runs every email account without leaving the Overview.
 * Single-domain plans start expanded. Plans with no mailbox pool render
 * nothing.
 */
export function PlanMailboxes({ e }: { e: EnrollmentDto }) {
  const [openId, setOpenId] = React.useState<number | null>(
    e.domains.length === 1 ? e.domains[0].id : null
  );

  // Nothing to manage: no mailbox pool (e.g. portfolio) or no domains yet.
  if (e.pool.mailboxes_pool === 0 || e.domains.length === 0) return null;

  return (
    <div className="px-4 pb-2 sm:px-5">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Email accounts
      </p>
      <div className="space-y-1.5">
        {e.domains.map(d => (
          <DomainMailboxRow
            key={d.id}
            d={d}
            open={openId === d.id}
            onToggle={() => setOpenId(prev => (prev === d.id ? null : d.id))}
            poolFallback={e.pool.mailboxes_pool}
          />
        ))}
      </div>
    </div>
  );
}

function DomainMailboxRow({
  d, open, onToggle, poolFallback
}: {
  d: ContainerSummaryDto;
  open: boolean;
  onToggle: () => void;
  poolFallback: number;
}) {
  // Per-domain allocation wins; an unallocated domain falls back to the
  // plan pool so the limit chip never reads "of 0".
  const allocated = d.mailboxes_allocated > 0 ? d.mailboxes_allocated : poolFallback;
  const unlimited = allocated < 0;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-brand-accent/5"
      >
        <AtSign className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{d.domain_name}</span>

        {d.has_twentyi_package ? (
          <span className="hidden shrink-0 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs font-medium text-muted-foreground sm:inline">
            {d.mailboxes_used} of {unlimited ? "∞" : allocated} used
          </span>
        ) : (
          <span className="hidden shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 sm:inline">
            Setting up
          </span>
        )}

        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-border bg-muted/10 p-3 sm:p-4">
          <MailboxList
            containerId={d.id}
            domainName={d.domain_name}
            hasPackage={d.has_twentyi_package}
            allocated={allocated}
          />
        </div>
      )}
    </div>
  );
}
